import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../../helpers/axiosInstance";
import { toast } from "react-toastify";

const initialState = {
    acsParametersData: [],
    pagination: null,
    loading: false,
    error: null,
    acsParametersNameList: [],
    messageInclusionsList: [],
    messageVersionsList: [],
    dsConfigurationList: [],
};

export const getAllAcsParameters = createAsyncThunk(
    "acs-parameters-management/acs-parameters",
    async ({ user, pagination, orderBy, search }) => {
        try {
            const { url, endpoint } = user?.routesConfig;
            const { pageSize, currentPage } = pagination;
            const { sortColumn, isAsce } = orderBy;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.searchacsparameter;

            const apiURL = base + endpointpath + `${pageSize}/${currentPage}/${sortColumn}/${isAsce}`;

            const response = await axiosInstance.post(apiURL, search);

            if(response?.data?.responseCode === '404') {
                toast.error(response?.data?.responseDescription);
            }

            return response?.data;
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

export const getAllAcsParametersName = createAsyncThunk(
    "acs-parameters-management/acs-parameters-name",
    async (user) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.getacsparameterdropdown;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.get(apiURL);
            return response?.data;
        } catch (error) {
            throw error.response;
        }
    }
);

export const getMessageInclusionsList = createAsyncThunk(
    "acs-parameters-management/message-inclusions",
    async (user) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.getmessageinclusion;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.get(apiURL);
            return response?.data;
        } catch (error) {
            throw error.response;
        }
    }
);

export const getMessageVersionsList = createAsyncThunk(
    "acs-parameters-management/message-versions",
    async (user) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.getmessageversion;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.get(apiURL);
            return response?.data;
        } catch (error) {
            throw error.response;
        }
    }
);

export const getDsConfigurationList = createAsyncThunk(
    "acs-parameters-management/ds-Configuration",
    async (user) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.getdsconfiguration;

            const apiURL = base + endpointpath;

            const response = await axiosInstance.get(apiURL);
            return response?.data;
        } catch (error) {
            throw error.response;
        }
    }
);

export const postAddUpdateParameter = createAsyncThunk(
    "acs-parameters-management/add-update-parameter",
    async ({ user, body, update }) => {
        try {
            const { url, endpoint } = user?.routesConfig;

            const base = url?.acsbackofficemanagement;
            const endpointpath = endpoint?.acsbackofficemanagement?.addOrUpdateAcsparameter;
            const action = update ? 'edit' : 'add';

            const apiURL = base + endpointpath + action;

            const method = update ? 'post' : 'post';
            const response = await axiosInstance[method](apiURL, body);

            if (response?.data?.responseCode === '99') {
                toast.error(response?.data?.responseDescription);
            } else if (response?.data?.responseCode === '403') {
                toast.error(response?.data?.responseDescription);
                return { status: response?.data?.responseCode };
            } else {
                toast.success('Request has been initiated and sent to checker');
                return { ...response?.data, status: response.status };
            }
        } catch (error) {
            toast.error(error.response.data.message);
            throw error.response;
        }
    }
);

const acsParametersSlice = createSlice({
    name: "acs-parameters",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // GET ALL ACS PARAMETERS
            .addCase(getAllAcsParameters.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getAllAcsParameters.fulfilled, (state, action) => {
                const { data, currentPage, pageSize, totalItem, totalPages } = action?.payload;
                state.loading = false;
                state.acsParametersData = data || [];
                state.pagination = {
                    currentPage: currentPage || 0,
                    pageSize: pageSize || 20,
                    totalItem,
                    totalPages
                };
            })
            .addCase(getAllAcsParameters.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })

            // GET ALL ACS PARAMETERS NAME
            .addCase(getAllAcsParametersName.fulfilled, (state, action) => {
                state.acsParametersNameList = action?.payload?.data?.map((e) => ({ ...e, label: e?.parameterName, value: e?.id, regex: e?.regex }));
            })

            // GET ALL MESSAGE INCLUSIONS
            .addCase(getMessageInclusionsList.fulfilled, (state, action) => {
                state.messageInclusionsList = action?.payload?.data?.filter(e => e.isActive).map(({ name, id }) => ({ label: name, value: id }));
            })

            // GET ALL MESSAGE VERSIONS
            .addCase(getMessageVersionsList.fulfilled, (state, action) => {
                state.messageVersionsList = action?.payload?.data?.filter(e => e.isActive).map((e) => ({ ...e, label: e?.version, value: e?.id }));
            })

            // GET ALL DIRECTORY SERVERS
            .addCase(getDsConfigurationList.fulfilled, (state, action) => {
                state.dsConfigurationList = action?.payload?.data?.filter(e => e.isActive).map((e) => ({ ...e, label: e?.dsName, value: e?.id }));
            })

            // ADD UPDATE PARAMETER
            .addCase(postAddUpdateParameter.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(postAddUpdateParameter.fulfilled, (state, action) => {
                state.loading = false;
            })
            .addCase(postAddUpdateParameter.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
    },
});

export default acsParametersSlice.reducer;